import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { Project } from 'src/app/core/objects/project';
import { NotificationsService } from 'src/app/core/services/notifications.service';
import { ProjectApiService } from 'src/app/core/services/project-api.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { InterlayerConfig, PalletLayerGroup, StackingOptions } from 'src/app/core/types';
import { LoadInterlayersComponent } from 'src/app/main-panel/add-load/components/load-interlayers/load-interlayers.component';
import { LoadPalletArrangementComponent } from 'src/app/main-panel/add-load/components/load-pallet-arrangement/load-pallet-arrangement.component';
import {
  buildEditablePalletLayerRowsFromProject,
  buildLayerQuantityDeltas,
  buildLoadFromPalletLayerGroup,
  buildPalletLayerGroupEditRequest,
  EditablePalletLayerRow,
  palletInterlayerToInterlayerConfig,
} from 'src/app/shared/helpers/pallet-layer-add.helper';
import { maxInterlayerFrequencyLayers } from 'src/app/shared/helpers/pallet-interlayer-display.helper';
import { validateZero } from 'src/app/core/validators/validators';

type EditTab = 'stacking' | 'layers' | 'interlayers';

@Component({
  selector: 'gl-loads-list-pallet-layer-group-edit',
  templateUrl: './loads-list-pallet-layer-group-edit.component.html',
  styleUrls: ['./loads-list-pallet-layer-group-edit.component.scss'],
  standalone: false,
})
export class LoadsListPalletLayerGroupEditComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() public palletGroup: PalletLayerGroup;
  @Input() public workSpaceIndex: number;
  @Input() public stopIndex: number;
  @Output() public closeComponent = new EventEmitter<void>();
  @Output() public panelLayoutChange = new EventEmitter<void>();

  @ViewChild('interlayersForm', { static: false })
  private interlayersForm?: LoadInterlayersComponent;
  @ViewChild('arrangementRef', { static: false })
  private arrangementRef?: LoadPalletArrangementComponent;

  public project: Project;
  public form: UntypedFormGroup;
  public stackingOptions: StackingOptions;
  public currentType: number;
  public activeTab: EditTab = 'layers';
  public editRows: EditablePalletLayerRow[] = [];
  public layersLoading = false;
  public maxFrequencyLayers = 1;
  public renderInterlayersForm = true;
  public highlightSaveButton = false;

  private subscriptions: Subscription[] = [];
  private arrangementFormSubscription?: Subscription;
  private interlayerFormSubscription?: Subscription;
  private currentWorkSpaceId: number;
  private layersCalculated = false;

  constructor(
    private projectService: ProjectService,
    private projectApiService: ProjectApiService,
    private notificationsService: NotificationsService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.maxFrequencyLayers = maxInterlayerFrequencyLayers(this.palletGroup);
    this.initForm();
    this.subscriptions.push(
      this.projectService.project.subscribe((project) => {
        this.project = project;
        this.currentWorkSpaceId = project.workSpaces[this.workSpaceIndex]?.id;
        this.ensureArrangementLayersLoaded();
      }),
    );
    this.subscriptions.push(
      this.projectService.currentWorkSpaceIndex.subscribe((index) => {
        this.currentWorkSpaceId = this.project?.workSpaces?.[index]?.id;
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.palletGroup && this.palletGroup) {
      this.maxFrequencyLayers = maxInterlayerFrequencyLayers(this.palletGroup);
      this.initForm();
      this.layersCalculated = false;
      this.editRows = [];
      this.highlightSaveButton = false;
      this.remountInterlayersForm();
      this.ensureArrangementLayersLoaded();
    }
  }

  ngAfterViewInit(): void {
    this.syncInterlayerFormFromInput();
    this.watchInterlayerFormChanges();
  }

  ngOnDestroy(): void {
    this.arrangementFormSubscription?.unsubscribe();
    this.interlayerFormSubscription?.unsubscribe();
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public openTab(tab: EditTab): void {
    this.activeTab = tab;
    this.ensureArrangementLayersLoaded();
    this.emitPanelLayoutChange();
  }

  public onSubmit(event: Event): void {
    event.preventDefault();
    if (!this.project || !this.palletGroup) {
      return;
    }

    if (this.interlayersForm?.form?.dirty && this.interlayersForm.hasValidationErrors()) {
      this.interlayersForm.triggerValidation();
      this.openTab('interlayers');
      return;
    }

    const workSpace = this.project.workSpaces[this.workSpaceIndex];
    const layers =
      this.layersCalculated && this.arrangementRef && this.editRows.length
        ? buildLayerQuantityDeltas(this.editRows, this.arrangementRef.getEditLayerCounts())
        : [];

    const interlayerCfg = this.interlayersForm?.getResolvedInterlayerConfig();
    const interlayerPayload = this.hasInterlayerChanges(interlayerCfg) ? interlayerCfg : undefined;

    const body = buildPalletLayerGroupEditRequest({
      currentWorkSpaceId: this.currentWorkSpaceId,
      workSpaceId: workSpace.id,
      stopId:
        this.stopIndex !== undefined && this.stopIndex !== null && this.stopIndex >= 0
          ? workSpace.stops?.[this.stopIndex]?.id
          : undefined,
      layerGroupId: this.palletGroup.id,
      layers: layers.length ? layers : undefined,
      interlayer: interlayerPayload ?? undefined,
      interlayerColor: this.palletGroup.interlayer?.color ?? this.palletGroup.color,
      interlayerWeight: this.palletGroup.interlayer?.weight ?? 0,
    });

    if (!body.layers?.length && !body.interlayer) {
      this.closeComponent.emit();
      return;
    }

    this.projectApiService.editPalletLayerGroup(body).subscribe((result) => {
      this.notificationsService.generateCOGNotification(result);
      this.closeComponent.emit();
    });
  }

  public onClose(): void {
    this.closeComponent.emit();
  }

  private initForm(): void {
    const group = this.palletGroup;
    const dims = group.dimensions;
    const qty = group.quantity ?? 0;
    const weight = group.weight ?? 0;
    this.currentType = group.loadType ?? 0;
    this.stackingOptions = {
      stacking: group.stackingOptions?.stacking ?? false,
      maxLoadsOnTop: group.stackingOptions?.maxLoadsOnTop,
      maxWeightOnTop: group.stackingOptions?.maxWeightOnTop,
      boxesBelowAllowed: group.stackingOptions?.boxesBelowAllowed,
      alongFloor: group.stackingOptions?.alongFloor,
      pctSupport: group.stackingOptions?.pctSupport,
      formation: group.stackingOptions?.formation,
    };
    this.form = new UntypedFormGroup({
      name: new UntypedFormControl(this.translate.instant(group.name ?? ''), [
        Validators.required,
        Validators.maxLength(255),
      ]),
      length: new UntypedFormControl(dims?.length, [Validators.required, validateZero]),
      width: new UntypedFormControl(dims?.width, [Validators.required, validateZero]),
      height: new UntypedFormControl(dims?.height, [Validators.required, validateZero]),
      weight: new UntypedFormControl(weight, []),
      quantity: new UntypedFormControl(qty, [Validators.required, validateZero]),
      stacking: new UntypedFormControl(this.stackingOptions.stacking),
      sumOfWeights: new UntypedFormControl(_.round(qty * weight, 2), []),
      diameter: new UntypedFormControl(dims?.diameter, [Validators.required, validateZero]),
    });
    this.form.disable({ emitEvent: false });
  }

  private ensureArrangementLayersLoaded(): void {
    if (this.project && !this.layersCalculated && !this.layersLoading) {
      this.loadArrangementLayers();
    }
  }

  private loadArrangementLayers(): void {
    if (!this.project || this.layersLoading) {
      return;
    }

    const cachedRows = buildEditablePalletLayerRowsFromProject(
      this.project,
      this.workSpaceIndex,
      this.stopIndex,
      this.palletGroup,
    );
    if (cachedRows.length > 0) {
      this.applyArrangementLayers(cachedRows);
      return;
    }

    this.layersLoading = true;
    this.emitPanelLayoutChange();
    const load = buildLoadFromPalletLayerGroup(this.palletGroup);
    this.projectApiService
      .calculateLayer({
        currentWorkSpaceId: this.currentWorkSpaceId,
        load,
      })
      .subscribe({
        next: (project) => {
          this.layersLoading = false;
          if (!project) {
            this.emitPanelLayoutChange();
            return;
          }
          const editRows = buildEditablePalletLayerRowsFromProject(
            project,
            this.workSpaceIndex,
            this.stopIndex,
            this.palletGroup,
          );
          if (editRows.length > 0) {
            this.applyArrangementLayers(editRows);
            return;
          }
          this.emitPanelLayoutChange();
        },
        error: () => {
          this.layersLoading = false;
          this.emitPanelLayoutChange();
        },
      });
  }

  private applyArrangementLayers(editRows: EditablePalletLayerRow[]): void {
    this.editRows = editRows;
    this.layersCalculated = true;
    this.emitPanelLayoutChange();
    queueMicrotask(() => this.watchArrangementFormChanges());
  }

  private watchArrangementFormChanges(): void {
    this.arrangementFormSubscription?.unsubscribe();
    const arrangementForm = this.arrangementRef?.editForm;
    if (!arrangementForm) {
      return;
    }
    this.arrangementFormSubscription = arrangementForm.valueChanges.subscribe(() =>
      this.updateHighlightSaveButton(),
    );
  }

  private watchInterlayerFormChanges(): void {
    this.interlayerFormSubscription?.unsubscribe();
    const interlayerForm = this.interlayersForm?.form;
    if (!interlayerForm) {
      return;
    }
    this.interlayerFormSubscription = interlayerForm.valueChanges.subscribe(() =>
      this.updateHighlightSaveButton(),
    );
  }

  private updateHighlightSaveButton(): void {
    const interlayerCfg = this.interlayersForm?.getResolvedInterlayerConfig();
    this.highlightSaveButton =
      this.hasLayerCountChanges() || this.hasInterlayerChanges(interlayerCfg);
  }

  private hasLayerCountChanges(): boolean {
    if (!this.layersCalculated || !this.arrangementRef || !this.editRows.length) {
      return false;
    }
    const counts = this.arrangementRef.getEditLayerCounts();
    return this.editRows.some(
      (row, index) => row.saveable && counts[index] !== row.layersLoadedQuantity,
    );
  }

  private hasInterlayerChanges(cfg: InterlayerConfig | undefined): boolean {
    if (!cfg) {
      return false;
    }
    if (!this.palletGroup.interlayer) {
      return true;
    }
    const initial = palletInterlayerToInterlayerConfig(this.palletGroup.interlayer);
    return (
      cfg.thickness !== initial.thickness ||
      cfg.everyNLayers !== initial.everyNLayers ||
      cfg.placeLastOnTopLayer !== initial.placeLastOnTopLayer ||
      cfg.placeLastOnFirstLayer !== initial.placeLastOnFirstLayer ||
      (cfg.name?.trim() || '') !== (initial.name?.trim() || '')
    );
  }

  private emitPanelLayoutChange(): void {
    queueMicrotask(() => this.panelLayoutChange.emit());
  }

  private syncInterlayerFormFromInput(): void {
    this.interlayersForm?.syncFromInterlayerForListEdit(this.palletGroup?.interlayer ?? null);
  }

  private remountInterlayersForm(): void {
    this.renderInterlayersForm = false;
    queueMicrotask(() => {
      this.renderInterlayersForm = true;
      queueMicrotask(() => {
        this.syncInterlayerFormFromInput();
        this.watchInterlayerFormChanges();
      });
    });
  }
}
